import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Alert,
  Chip,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { formatCurrency } from "@nomnom/core";
import {
  formatTimestamp,
  loadWalletData as fetchWalletData,
  type WalletRow,
  type WalletTxRow,
} from "../services/walletsService";

export default function Wallets() {
  const [wallets, setWallets] = useState<WalletRow[]>([]);
  const [transactions, setTransactions] = useState<WalletTxRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [uidFilter, setUidFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadWalletData = async () => {
      try {
        setLoading(true);
        setError(null);
        const { wallets: walletRows, transactions: txRows } =
          await fetchWalletData();
        setWallets(walletRows);
        setTransactions(txRows);
      } catch (error) {
        console.error("[Wallets] Failed to load wallet data:", error);
        setError(
          error instanceof Error ? error.message : "Failed to load wallet data.",
        );
      } finally {
        setLoading(false);
      }
    };

    void loadWalletData();
  }, []);

  const visibleTransactions = useMemo(() => {
    return transactions.filter((tx) => {
      const uidMatches = !uidFilter || tx.uid.includes(uidFilter.trim());
      const typeMatches = typeFilter === "all" || tx.type === typeFilter;
      return uidMatches && typeMatches;
    });
  }, [transactions, uidFilter, typeFilter]);

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Wallets
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Wallet Balances
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>User UID</TableCell>
                <TableCell align="right">Balance</TableCell>
                <TableCell align="right">Lifetime Top-up</TableCell>
                <TableCell align="right">Lifetime Spent</TableCell>
                <TableCell>Updated At</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {wallets.map((wallet) => (
                <TableRow key={wallet.id}>
                  <TableCell>{wallet.uid}</TableCell>
                  <TableCell align="right">{formatCurrency(wallet.balance)}</TableCell>
                  <TableCell align="right">
                    {formatCurrency(wallet.lifetimeTopUp)}
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(wallet.lifetimeSpent)}
                  </TableCell>
                  <TableCell>{formatTimestamp(wallet.updatedAt)}</TableCell>
                </TableRow>
              ))}
              {!wallets.length && !loading && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No wallet records found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            gap: 2,
          }}
        >
          <Typography variant="h6">Wallet Transaction Ledger</Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="UID filter"
              size="small"
              value={uidFilter}
              onChange={(e) => setUidFilter(e.target.value)}
            />
            <TextField
              label="Type"
              size="small"
              select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="topup">Top-up</MenuItem>
              <MenuItem value="debit">Debit</MenuItem>
              <MenuItem value="refund">Refund</MenuItem>
            </TextField>
          </Box>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Timestamp</TableCell>
                <TableCell>UID</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Order</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleTransactions.map((tx) => (
                <TableRow key={tx.id}>
                  <TableCell>{formatTimestamp(tx.createdAt)}</TableCell>
                  <TableCell>{tx.uid}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={tx.type}
                      color={
                        tx.type === "topup"
                          ? "success"
                          : tx.type === "refund"
                            ? "warning"
                            : "primary"
                      }
                    />
                  </TableCell>
                  <TableCell align="right">{formatCurrency(tx.amount)}</TableCell>
                  <TableCell>{tx.orderId || "-"}</TableCell>
                  <TableCell>{tx.status}</TableCell>
                </TableRow>
              ))}
              {!visibleTransactions.length && !loading && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No transactions found for current filters.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
